import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { UserRole } from '@apleona/shared';
import { api, tokenStore } from './api';

export interface Profile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone: string | null;
  role: UserRole;
  roleLabel: string;
  mustChangePassword: boolean;
  company: { id: string; name: string } | null;
  location: { id: string; name: string; street: string | null; zip: string | null; city: string | null } | null;
}

interface AuthState {
  profile: Profile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthState>({
  profile: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export const useAuth = () => useContext(Ctx);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tokenStore.get()) {
      setLoading(false);
      return;
    }
    api<Profile>('/auth/me')
      .then(setProfile)
      .catch(() => tokenStore.clear())
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api<{ accessToken: string; refreshToken: string; profile: Profile }>(
      '/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password, device: 'Fuhrpark-Portal' }) }
    );
    tokenStore.set(data.accessToken, data.refreshToken);
    setProfile(data.profile);
  }, []);

  const logout = useCallback(() => {
    const refreshToken = tokenStore.getRefresh();
    void api('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }).catch(
      () => {}
    );
    tokenStore.clear();
    setProfile(null);
  }, []);

  const value = useMemo(() => ({ profile, loading, login, logout }), [profile, loading, login, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
